import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input, Label, ListGroup, ListGroupItem, Badge, Spinner, FormGroup } from "reactstrap";
import { searchResearchSymbols } from "../../services/userPredictionService";

/**
 * Asset picker for the research wizard.
 *
 * The wizard used to offer a fixed <select> of the two dozen symbols the
 * scheduled jobs cover. This is a search box instead, with that curated list
 * demoted to the suggestions shown before anyone types.
 *
 * IT SUGGESTS; IT DOES NOT RESTRICT. Anything typed here is accepted, in the
 * box and by the server: a self-research prompt carries no market data, so the
 * member's own AI does the looking, and it can look up a ticker we have never
 * heard of. The catalogue behind the search is US listings plus Coinbase pairs,
 * which is a good shortlist and a poor limit — every Toronto, London and Hong
 * Kong listing is absent from it and researchable anyway.
 *
 * The parent still owns the chosen symbol; this only reports changes.
 */

const CLASS_LABEL = { crypto: "Crypto", etf: "ETF", equity: "Stock", stock: "Stock", index: "Index" };
const CLASS_COLOR = { crypto: "warning", etf: "info", equity: "primary", stock: "primary", index: "secondary" };

const SymbolPicker = ({ value, suggested = [], onChange, disabled = false }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMeta, setSelectedMeta] = useState(null);

  // Guards against a slow earlier request overwriting a newer one's results.
  const requestSeq = useRef(0);
  const blurTimer = useRef(null);

  const suggestions = useMemo(
    () => suggested.map((a) => ({ ...a, assetClass: a.assetClass || "equity" })),
    [suggested]
  );

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      setSearching(false);
      return undefined;
    }

    setSearching(true);
    const seq = ++requestSeq.current;
    // Typing a ticker takes a few keystrokes; waiting for a pause keeps one
    // provider call per symbol rather than one per letter.
    const timer = setTimeout(async () => {
      try {
        const res = await searchResearchSymbols(q);
        if (seq !== requestSeq.current) return;
        setResults(res.data || []);
        setError(null);
      } catch (e) {
        if (seq !== requestSeq.current) return;
        setResults([]);
        setError("Symbol lookup is unavailable right now — type a ticker and generate anyway.");
      } finally {
        if (seq === requestSeq.current) setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => () => clearTimeout(blurTimer.current), []);

  // The parent preselects a symbol before anyone has picked one. Recover its
  // name from the suggestions so the chip reads "BTC · Bitcoin" on first paint
  // rather than "BTC · Selected".
  useEffect(() => {
    if (!value || selectedMeta?.symbol === value) return;
    const known = suggestions.find((a) => a.symbol === value);
    if (known) setSelectedMeta(known);
  }, [value, suggestions, selectedMeta]);

  const choose = useCallback(
    (asset) => {
      setSelectedMeta(asset);
      setQuery("");
      setResults([]);
      setOpen(false);
      onChange(asset.symbol, asset);
    },
    [onChange]
  );

  // Typing a ticker and tabbing away should still work even if the lookup is
  // down, so the raw text is accepted as a symbol on blur.
  const commitFreeText = () => {
    const raw = query.trim().toUpperCase();
    if (raw && raw !== value) {
      setSelectedMeta(null);
      onChange(raw, null);
    }
  };

  const shown = query.trim() ? results : suggestions;
  // No count when there is nothing to count: "0 matches" reads as a rejection
  // above a row that is offering to use exactly what was typed.
  const listLabel = query.trim()
    ? (results.length
      ? `${results.length} match${results.length === 1 ? "" : "es"}`
      : "Not in our shortlist — you can still use it")
    : "Covered by our own daily research";

  return (
    <FormGroup>
      <Label for="asset-search">Asset</Label>

      {value && (
        <div className="mb-2 d-flex align-items-center gap-2">
          <Badge color={CLASS_COLOR[selectedMeta?.assetClass] || "primary"} className="fs-13">
            {value}
          </Badge>
          <span className="text-muted fs-13 text-truncate">
            {selectedMeta?.name || "Selected"}
            {selectedMeta?.exchange ? ` · ${selectedMeta.exchange}` : ""}
          </span>
        </div>
      )}

      <div className="position-relative">
        <Input
          id="asset-search"
          type="search"
          autoComplete="off"
          disabled={disabled}
          placeholder="Search any ticker or company — NVDA, Palantir, SOL…"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onBlur={() => {
            // Deferred so a click on a result lands before the list unmounts.
            blurTimer.current = setTimeout(() => {
              setOpen(false);
              commitFreeText();
            }, 150);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && shown.length) {
              e.preventDefault();
              choose(shown[0]);
            } else if (e.key === "Escape") {
              setOpen(false);
            }
          }}
        />

        {searching && (
          <Spinner size="sm" className="position-absolute end-0 top-50 translate-middle-y me-3" />
        )}

        {open && (
          <div
            className="position-absolute w-100 bg-body border rounded shadow mt-1"
            style={{ zIndex: 5, maxHeight: 320, overflowY: "auto" }}
          >
            <div className="px-3 py-2 text-muted fs-12 border-bottom">{listLabel}</div>
            <ListGroup flush>
              {shown.map((a) => (
                <ListGroupItem
                  key={`${a.symbol}-${a.exchange || ""}`}
                  tag="button"
                  type="button"
                  action
                  // onMouseDown fires before the input's blur, so the choice is
                  // registered instead of being swallowed by commitFreeText.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(a);
                  }}
                  className="d-flex justify-content-between align-items-center text-start"
                >
                  <span className="text-truncate">
                    <strong>{a.symbol}</strong>
                    <span className="text-muted ms-2 fs-13">{a.name}</span>
                  </span>
                  <span className="flex-shrink-0 ms-2">
                    {a.exchange && <span className="text-muted fs-12 me-2">{a.exchange}</span>}
                    <Badge color={CLASS_COLOR[a.assetClass] || "primary"} className="fs-11">
                      {CLASS_LABEL[a.assetClass] || a.assetClass}
                    </Badge>
                  </span>
                </ListGroupItem>
              ))}
              {/* No match is not a refusal.
                  The list is a shortcut for finding a ticker, not the set of
                  tickers allowed: anything typed here is researched, because
                  the prompt carries no market data and the member's own AI does
                  the looking. The old copy said we could only research what
                  TwelveData or Coinbase carried, which was wrong on both counts
                  -- member research left TwelveData behind, and nothing is
                  gated on the catalogue any more. */}
              {!shown.length && !searching && query.trim() && (
                <ListGroupItem
                  tag="button"
                  type="button"
                  action
                  className="fs-13"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose({ symbol: query.trim().toUpperCase(), name: null })}
                >
                  Use <strong>{query.trim().toUpperCase()}</strong>
                  <span className="text-muted ms-2">
                    not in our shortlist, and that is fine
                  </span>
                </ListGroupItem>
              )}
            </ListGroup>
          </div>
        )}
      </div>

      {error && <p className="text-warning fs-12 mt-1 mb-0">{error}</p>}
    </FormGroup>
  );
};

export default SymbolPicker;
